import { useTitleStore } from 'src/stores/useTitleStore';
import * as React from 'react';
import { useIntl } from 'react-intl';
import { Button } from 'src/registry/ui/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { booleanBadgeOptions, ICON_MAP } from 'src/utils/constants';
import { Link, useParams } from 'react-router-dom';
import { servicePlansMessages } from 'src/features/servicePlans/utils/messages';
import { useGetServicePlan } from 'src/features/servicePlans/api/useGetServicePlan';
import { commonMessages, generalMessages } from 'src/utils/messages';
import { SectionBlock, SectionBlockHeader, SectionBlockTitle } from 'src/registry/components/blocks/SectionBlock';
import DashboardGeneralBlock from 'src/features/billing/components/DashboardGeneralBlock';
import { Badge } from 'src/registry/ui/Badge';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import DeleteServicePlanFormModal from 'src/features/servicePlans/components/forms/DeleteServicePlanFormModal';
import UpdateServicePlanFormModal from 'src/features/servicePlans/components/forms/UpdateServicePlanFormModal';
import ServicePlanDashboardTables from 'src/features/servicePlans/components/tables/ServicePlanDashboardTables';
import { getFormattedDateTime } from 'src/utils/getFormattedDateTime';
import { servicePlanTermsMessages } from 'src/features/servicePlanTerms/utils/messages';
import { CellCurrencyBadge } from 'src/features/moneyAccounts/components/tables/CellCurrencyBadge';
import DeactivateServicePlanFormModal from 'src/features/servicePlans/components/forms/DeactivateServicePlanFormModal';
import ActivateServicePlanFormModal from 'src/features/servicePlans/components/forms/ActivateServicePlanFormModal';
import useHasPermissions from 'src/utils/useHasPermissions';
import { PERMISSION } from 'src/features/auth/types';
import { billingSimulationMessages } from 'src/features/billingSimulation/utils/messages';

const ServicePlanDashboard = () => {
    const { formatMessage } = useIntl();
    const { servicePlanId = '' } = useParams();

    const permissionManageServicePlans = useHasPermissions([PERMISSION.MANAGE_SERVICE_PLANS]);

    const { setTitle, removeTitle } = useTitleStore();

    React.useEffect(() => {
        setTitle(formatMessage(servicePlansMessages.servicePlanDashboard));
        return removeTitle;
    }, [formatMessage, removeTitle, setTitle]);

    const { data: servicePlanData } = useGetServicePlan({
        id: servicePlanId,
    });

    const isActiveAsString = servicePlanData?.is_active ? 'true' : 'false';

    const dashboardBlockData = {
        section1: [
            {
                title: servicePlansMessages.code,
                value: servicePlanData?.code ?? '-',
            },
            {
                title: servicePlansMessages.category,
                value: servicePlanData?.category?.name ?? '-',
            },
            {
                title: servicePlansMessages.isActive,
                value: (
                    <Badge theme={booleanBadgeOptions[isActiveAsString].theme || 'gray'}>
                        <FontAwesomeIcon icon={booleanBadgeOptions[isActiveAsString].icon as IconProp} />
                        {formatMessage(booleanBadgeOptions[isActiveAsString].label)}
                    </Badge>
                ),
            },
            {
                title: servicePlansMessages.externalServicePlan,
                value: servicePlanData?.external_service_plan_id ?? '-',
            },
            {
                title: servicePlansMessages.invoiceName,
                value: servicePlanData?.invoice_name ?? '-',
            },
        ],
        section2: [
            {
                title: servicePlansMessages.currentTermsValidFrom,
                value: getFormattedDateTime(servicePlanData?.current_terms?.valid_from)?.formattedDate ?? '-',
            },
            {
                title: servicePlansMessages.currentTermsValidTo,
                value: getFormattedDateTime(servicePlanData?.current_terms?.valid_to)?.formattedDate ?? '-',
            },
            {
                title: commonMessages.currency,
                value: servicePlanData?.current_terms?.currency ? (
                    <CellCurrencyBadge currency={servicePlanData?.current_terms?.currency} />
                ) : (
                    '-'
                ),
            },
            {
                title: servicePlanTermsMessages.trialPeriodDays,
                value: servicePlanData?.current_terms?.trial_period_days ?? '-',
            },
            {
                title: servicePlansMessages.lockWarningDays,
                value: servicePlanData?.current_terms?.lock_warning_days ?? '-',
            },
            {
                title: servicePlansMessages.lockGraceDays,
                value: servicePlanData?.current_terms?.lock_grace_period_days ?? '-',
            },
            {
                title: servicePlansMessages.stopWarningDays,
                value: servicePlanData?.current_terms?.stop_warning_days ?? '-',
            },
            {
                title: servicePlansMessages.stopGraceDays,
                value: servicePlanData?.current_terms?.stop_grace_period_days ?? '-',
            },
        ],
        section3: [
            {
                title: generalMessages.description,
                value: servicePlanData?.description ?? '-',
            },
        ],
    };

    return (
        <>
            <Button asChild className="place-self-start p-0" variant="link">
                <Link to={`/service-plans`}>
                    <FontAwesomeIcon icon={ICON_MAP.BACK} className="opacity-50" />
                    {formatMessage(servicePlansMessages.backToAllServicePlans)}
                </Link>
            </Button>

            <div className="mt-6 grid grid-cols-1 gap-8">
                <SectionBlock>
                    <SectionBlockHeader className="flex min-h-9 flex-row justify-between">
                        <SectionBlockTitle className="text-2xl whitespace-nowrap">
                            {servicePlanData?.name}
                        </SectionBlockTitle>

                        {servicePlanData && (
                            <div className="flex flex-row gap-2">
                                <Button asChild size="mdIcon" variant="outline" className="cursor-pointer">
                                    <Link to={`/service-plans/${servicePlanId}/simulate-billing`}>
                                        <FontAwesomeIcon icon={ICON_MAP.WAVE_PULSE} />
                                        <span className="hidden md:block">
                                            {formatMessage(billingSimulationMessages.simulateBilling)}
                                        </span>
                                    </Link>
                                </Button>
                                {permissionManageServicePlans && servicePlanData?.is_active && (
                                    <DeactivateServicePlanFormModal
                                        selectedServicePlan={servicePlanData}
                                        semantics={{
                                            entryName: formatMessage(servicePlansMessages.servicePlan).toLowerCase(),
                                        }}
                                    >
                                        <Button size="mdIcon" variant="destructiveLight" className="cursor-pointer">
                                            <FontAwesomeIcon icon={ICON_MAP.TOGGLE_OFF} />
                                            <span className="hidden md:block">
                                                {formatMessage(servicePlansMessages.deactivate)}
                                            </span>
                                        </Button>
                                    </DeactivateServicePlanFormModal>
                                )}
                                {permissionManageServicePlans && !servicePlanData?.is_active && (
                                    <ActivateServicePlanFormModal
                                        selectedServicePlan={servicePlanData}
                                        semantics={{
                                            entryName: formatMessage(servicePlansMessages.servicePlan).toLowerCase(),
                                        }}
                                    >
                                        <Button size="mdIcon" variant="default" className="cursor-pointer">
                                            <FontAwesomeIcon icon={ICON_MAP.TOGGLE_ON} />
                                            <span className="hidden md:block">
                                                {formatMessage(servicePlansMessages.activate)}
                                            </span>
                                        </Button>
                                    </ActivateServicePlanFormModal>
                                )}

                                {permissionManageServicePlans && (
                                    <UpdateServicePlanFormModal
                                        selectedServicePlan={servicePlanData}
                                        semantics={{
                                            entryName: formatMessage(servicePlansMessages.plan).toLowerCase(),
                                        }}
                                    >
                                        <Button size="mdIcon" variant="outline" className="cursor-pointer">
                                            <FontAwesomeIcon icon={ICON_MAP.EDIT} />
                                            <span className="hidden md:block">
                                                {formatMessage(commonMessages.edit)}
                                            </span>
                                        </Button>
                                    </UpdateServicePlanFormModal>
                                )}
                                {permissionManageServicePlans && !servicePlanData?.is_active && (
                                    <DeleteServicePlanFormModal
                                        selectedServicePlan={servicePlanData}
                                        semantics={{
                                            entryName: formatMessage(servicePlansMessages.plan).toLowerCase(),
                                        }}
                                    >
                                        <Button size="mdIcon" variant="destructiveLight" className="cursor-pointer">
                                            <FontAwesomeIcon icon={ICON_MAP.DELETE} />
                                            <span className="hidden md:block">
                                                {formatMessage(generalMessages.delete)}
                                            </span>
                                        </Button>
                                    </DeleteServicePlanFormModal>
                                )}
                            </div>
                        )}
                    </SectionBlockHeader>

                    <DashboardGeneralBlock dashboardData={dashboardBlockData} indexForLastSeparator={2} />
                </SectionBlock>
                <ServicePlanDashboardTables isServicePlanActive={Boolean(servicePlanData?.is_active)} />
            </div>
        </>
    );
};

export default ServicePlanDashboard;
